import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
const KEY = 'pos.theme';

export function readStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

let mode: ThemeMode = readStoredTheme();
const listeners = new Set<() => void>();

function systemDark(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(m: ThemeMode): void {
  const dark = m === 'dark' || (m === 'system' && systemDark());
  document.documentElement.classList.toggle('dark', dark);
}

function setMode(next: ThemeMode) {
  mode = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* ignore */
  }
  applyTheme(next);
  listeners.forEach((l) => l());
}

/** Light / dark / system. Persisted in localStorage; `.dark` class on <html> drives the CSS tokens. */
export function useTheme() {
  const current = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => mode,
    () => mode,
  );

  useEffect(() => {
    applyTheme(current);
    if (current !== 'system' || typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [current]);

  const cycle = useCallback(() => {
    setMode(current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light');
  }, [current]);

  return { mode: current, setMode, cycle };
}
